import { useAtomValue } from "jotai";
import { useCallback, useRef, useState } from "react";
import { useCreatePortfolioLeadMutation } from "web_myplann/Api/Portfolio/useCreatePortfolioLeadMutation";
import { useRecordAlertBoxActionMutation } from "web_myplann/Api/Portfolio/useRecordAlertBoxActionMutation";
import {
	AlertBoxActionEnum,
	AlertBoxActionStateEnum,
	type PortfolioAlertBoxDTO,
	type PortfolioDetailResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { buildRefinancingPrefill } from "web_myplann/NotificationCenter/EventAlert/refinancingPrefill";
import { supportedNavigationTypesState } from "web_myplann/Shared/State/appInfoAtom";
import { isNavigationSupported } from "web_myplann/Shared/Utils/isNavigationSupported";
import { device, NavigationTypes } from "web_myplann/Utils/device";

/**
 * Dispatches Event Alert CTA actions.
 *
 * - "Talk to agent" opens the shared {@link ContactAgentDialog}; confirming it
 *   creates an advisor lead for the alert's contract.
 * - "Compare rates" opens the native refinancing calculator, prefilled from the
 *   contract; older shells without that screen get the calculator list.
 * - The insurance actions (renew / recalculate / sign new travel) have no
 *   arranger ("sjednávač") destination yet, so per spec they record the fallback
 *   "Opened" state on click, surfacing the "Naposledy otevřeno" subtext.
 *
 * "Check conditions" has no defined target yet, so it stays an intentional
 * no-op (SEAM).
 */
const INSURANCE_ACTIONS: ReadonlySet<AlertBoxActionEnum> = new Set([
	AlertBoxActionEnum.RenewInsurance,
	AlertBoxActionEnum.RecalculateInsurance,
	AlertBoxActionEnum.SignNewTravelInsurance,
]);

export const useEventActions = (
	contractId: number,
	portfolio?: PortfolioDetailResponseDTO | null,
) => {
	const [selectedAlert, setSelectedAlert] =
		useState<PortfolioAlertBoxDTO | null>(null);
	const { mutate: createLead } = useCreatePortfolioLeadMutation();
	const { mutate: recordAction } = useRecordAlertBoxActionMutation();
	const supportedNavigationTypes = useAtomValue(supportedNavigationTypesState);

	// The optimistic ContactAgentDialog can call onConfirm more than once while
	// closing; this makes the lead creation fire at most once per selection,
	// independent of state-update batching.
	const leadCreatedRef = useRef(false);

	const handleAction = useCallback(
		(action: AlertBoxActionEnum, alert: PortfolioAlertBoxDTO) => {
			if (action === AlertBoxActionEnum.TalkToAgent) {
				leadCreatedRef.current = false;
				setSelectedAlert(alert);
				return;
			}
			if (action === AlertBoxActionEnum.CompareRates) {
				if (
					!isNavigationSupported(
						supportedNavigationTypes,
						NavigationTypes.RefinancingCalculatorScreen,
					)
				) {
					device.navigationRequest(NavigationTypes.CalculatorListScreen);
					return;
				}
				const prefill = portfolio ? buildRefinancingPrefill(portfolio) : null;
				device.navigationRequest(
					NavigationTypes.RefinancingCalculatorScreen,
					undefined,
					prefill ?? undefined,
				);
				return;
			}
			if (INSURANCE_ACTIONS.has(action)) {
				recordAction({
					contractId,
					eventType: alert.eventType,
					eventDate: alert.eventDate,
					action,
					state: AlertBoxActionStateEnum.Opened,
				});
			}
		},
		[contractId, portfolio, recordAction, supportedNavigationTypes],
	);

	const closeContactDialog = useCallback(() => setSelectedAlert(null), []);

	const confirmLead = useCallback(() => {
		if (!selectedAlert || leadCreatedRef.current) {
			return;
		}
		leadCreatedRef.current = true;
		createLead({
			contractId,
			eventType: selectedAlert.eventType,
			eventDate: selectedAlert.eventDate,
		});
		setSelectedAlert(null);
	}, [contractId, createLead, selectedAlert]);

	return {
		handleAction,
		contactDialogOpen: selectedAlert !== null,
		closeContactDialog,
		confirmLead,
	};
};
